import { useState } from "react";
import { useCustom } from "@refinedev/core";
import { StatusBadge } from "@fawkes/ui";
import { Download, FileText } from "lucide-react";

import { getAccessToken } from "../../auth";

type T = (key: string, options?: { args?: unknown[] }) => string;

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? "/api";

interface Bestand {
  id: string;
  bestandsnaam: string;
  contentType: string;
  grootte: number;
  scanStatus: "SCHOON" | "NIET_GESCAND" | "BESMET" | "NVT";
}

/**
 * De bestanden bij een DOCUMENT_UPLOAD (ontwerp hoofdstuk 18): met de uitkomst van de virusscan en alleen als bijlage
 * te downloaden. Bij goedkeuren gaan ze naar het dierdossier als de klant een dier koos.
 */
export function BestandenBlok({ t, verzoekId }: { t: T; verzoekId: string }) {
  const { data } = useCustom<Bestand[]>({ url: `klantverzoeken/${verzoekId}/bestanden`, method: "get" });
  const [fout, setFout] = useState<string | null>(null);
  const bestanden = Array.isArray(data?.data) ? data.data : [];

  async function download(b: Bestand) {
    setFout(null);
    const token = getAccessToken();
    const res = await fetch(`${API_URL}/klantverzoeken/${verzoekId}/bestanden/${b.id}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    if (!res.ok) {
      setFout(t("klantverzoeken.bestanden.fout"));
      return;
    }
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement("a");
    a.href = url;
    a.download = b.bestandsnaam;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  }

  return (
    <div className="kvd__prijs">
      <h3>{t("klantverzoeken.bestanden.titel")}</h3>
      <ul className="kpt__lijst">
        {bestanden.map((b) => (
          <li key={b.id}>
            <FileText size={16} aria-hidden="true" />
            <span className="kpt__wie">
              <strong>{b.bestandsnaam}</strong>
              <span className="kvd__stil">{b.contentType} · {Math.max(1, Math.round(b.grootte / 1024))} kB</span>
            </span>
            <StatusBadge tone={b.scanStatus === "SCHOON" ? "success" : b.scanStatus === "BESMET" ? "danger" : "warning"}>
              {t(`klantverzoeken.bestanden.scan.${b.scanStatus}`)}
            </StatusBadge>
            {b.scanStatus !== "BESMET" && (
              <button type="button" className="kpt__link" onClick={() => void download(b)}>
                <Download size={14} aria-hidden="true" /> {t("klantverzoeken.bestanden.download")}
              </button>
            )}
          </li>
        ))}
      </ul>
      {fout && <p className="fawkes-error" role="alert">{fout}</p>}
    </div>
  );
}
